import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  message,
  Popconfirm,
  Input,
  Switch,
  Descriptions,
  Alert,
  Spin,
  Row,
  Col,
  Typography
} from 'antd';
import {
  DownloadOutlined,
  DeleteOutlined,
  ReloadOutlined,
  FolderOutlined,
  FileOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  SettingOutlined,
  EditOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text } = Typography;

interface SaveFile {
  name: string;
  size: number;
  modified: string;
  type: 'file' | 'directory';
  path: string;
  extension: string;
}

interface GameType {
  id: number;
  name: string;
  save_directory: string;
  save_extensions: string;
  display_all_files: boolean;
}

interface SaveManagementProps {
  serverId: number;
  gameType: string;
}

const SaveManagement: React.FC<SaveManagementProps> = ({ serverId, gameType }) => {
  const [saveFiles, setSaveFiles] = useState<SaveFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [gameTypeConfig, setGameTypeConfig] = useState<GameType | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [restoreModalVisible, setRestoreModalVisible] = useState(false);
  const [restoreFile, setRestoreFile] = useState<SaveFile | null>(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [showAllFiles, setShowAllFiles] = useState(true);
  const [fileExtensions, setFileExtensions] = useState<string[]>([]);
  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const [renameFile, setRenameFile] = useState<SaveFile | null>(null);
  const [newFileName, setNewFileName] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // 响应式检测
  const handleResize = useCallback(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // 获取游戏类型配置
  const fetchGameTypeConfig = useCallback(async () => {
    try {
      const encodedGameType = encodeURIComponent(gameType);
      const response = await api.get(`/game-types/${encodedGameType}`);
      setGameTypeConfig(response.data);
      setShowAllFiles(response.data.display_all_files || false);
      setFileExtensions(response.data.save_extensions ? response.data.save_extensions.split(',') : []);
    } catch (error) {
      console.error('获取游戏类型配置失败:', error);
    }
  }, [gameType]);

  // 获取存档文件列表
  const fetchSaveFiles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/servers/${serverId}/saves`);
      setSaveFiles(response.data);
    } catch (error: any) {
      message.error('获取存档文件失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  }, [serverId]);

  // 下载存档文件
  const downloadSaveFile = async (fileName: string) => {
    try {
      const response = await api.get(`/servers/${serverId}/saves/download/${fileName}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      message.success('存档文件下载成功');
    } catch (error: any) {
      message.error('下载存档文件失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 删除存档文件
  const deleteSaveFile = async (fileName: string) => {
    try {
      await api.delete(`/servers/${serverId}/saves/${fileName}`);
      message.success('存档文件删除成功');
      fetchSaveFiles();
    } catch (error: any) {
      message.error('删除存档文件失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 恢复存档
  const restoreSaveFile = async (fileName: string) => {
    setRestoreLoading(true);
    try {
      await api.post(`/servers/${serverId}/saves/restore`, { fileName });
      message.success('存档恢复成功，服务器将重启');
      fetchSaveFiles();
    } catch (error: any) {
      message.error('恢复存档失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setRestoreLoading(false);
      setRestoreModalVisible(false);
      setRestoreFile(null);
    }
  };

  // 批量删除
  const batchDeleteFiles = async () => {
    try {
      await api.post(`/servers/${serverId}/saves/batch-delete`, { fileNames: selectedFiles });
      message.success(`成功删除 ${selectedFiles.length} 个文件`);
      setSelectedFiles([]);
      fetchSaveFiles();
    } catch (error: any) {
      message.error('批量删除失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 更新游戏类型配置
  const updateGameTypeConfig = async () => {
    try {
      await api.put(`/game-types/${gameTypeConfig?.id}`, {
        save_directory: gameTypeConfig?.save_directory,
        save_extensions: fileExtensions.join(','),
        display_all_files: showAllFiles
      });
      message.success('配置更新成功');
      setConfigModalVisible(false);
      fetchGameTypeConfig();
    } catch (error: any) {
      message.error('更新配置失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 重命名文件
  const renameSaveFile = async () => {
    if (!renameFile || !newFileName.trim()) {
      message.error('请输入新的文件名');
      return;
    }

    // 检查是否包含中文字符
    const chineseRegex = /[\u4e00-\u9fff]/;
    if (chineseRegex.test(newFileName)) {
      message.error('文件名不能包含中文字符，会导致乱码问题');
      return;
    }

    // 检查文件名是否有效
    const invalidChars = /[<>:"/\\|?*]/;
    if (invalidChars.test(newFileName)) {
      message.error('文件名包含无效字符: < > : " / \\ | ? *');
      return;
    }

    setRenameLoading(true);
    try {
      await api.post(`/servers/${serverId}/saves/rename`, {
        oldFileName: renameFile.name,
        newFileName: newFileName.trim()
      });
      message.success('文件重命名成功');
      setRenameModalVisible(false);
      setRenameFile(null);
      setNewFileName('');
      fetchSaveFiles();
    } catch (error: any) {
      message.error('重命名文件失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setRenameLoading(false);
    }
  };

  // 打开重命名对话框
  const openRenameModal = (file: SaveFile) => {
    setRenameFile(file);
    setNewFileName(file.name);
    setRenameModalVisible(true);
  };

  // 格式化文件大小
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 格式化修改时间
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // 检查文件是否应该显示
  const shouldShowFile = (file: SaveFile) => {
    if (showAllFiles) return true;
    if (file.type === 'directory') return true;
    return fileExtensions.some(ext => file.name.toLowerCase().endsWith(ext.toLowerCase()));
  };

  // 过滤显示的文件
  const filteredFiles = saveFiles.filter(shouldShowFile);

  useEffect(() => {
    fetchGameTypeConfig();
    fetchSaveFiles();
  }, [serverId, gameType, fetchGameTypeConfig, fetchSaveFiles]);

  const columns = isMobile ? [
    // 移动端：卡片式布局
    {
      title: '文件信息',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: SaveFile) => (
        <div style={{ padding: '8px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            {record.type === 'directory' ? <FolderOutlined /> : <FileOutlined />}
            <Text strong={record.type === 'file'} style={{ marginLeft: '8px', flex: 1 }}>
              {text}
            </Text>
            {record.extension && (
              <Tag color="blue" style={{ marginLeft: '8px' }}>{record.extension}</Tag>
            )}
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.type === 'file' ? formatFileSize(record.size) : '文件夹'}
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <ClockCircleOutlined style={{ marginRight: '4px' }} />
              {formatDate(record.modified)}
            </Text>
          </div>
          
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {record.type === 'file' && (
              <>
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadSaveFile(record.name)}
                  size="small"
                  style={{ padding: '4px 8px', fontSize: '12px' }}
                >
                  下载
                </Button>
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => openRenameModal(record)}
                  size="small"
                  disabled={renameLoading}
                  style={{ padding: '4px 8px', fontSize: '12px' }}
                >
                  重命名
                </Button>
                <Button
                  type="link"
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setRestoreFile(record);
                    setRestoreModalVisible(true);
                  }}
                  size="small"
                  danger
                  disabled={restoreLoading}
                  style={{ padding: '4px 8px', fontSize: '12px' }}
                >
                  恢复
                </Button>
              </>
            )}
            <Popconfirm
              title="确定要删除这个文件吗？"
              onConfirm={() => deleteSaveFile(record.name)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                icon={<DeleteOutlined />}
                danger
                size="small"
                style={{ padding: '4px 8px', fontSize: '12px' }}
              >
                删除
              </Button>
            </Popconfirm>
          </div>
        </div>
      ),
    },
  ] : [
    // 桌面端：传统表格布局
    {
      title: '文件名',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: SaveFile) => (
        <Space>
          {record.type === 'directory' ? <FolderOutlined /> : <FileOutlined />}
          <Text strong={record.type === 'file'}>{text}</Text>
          {record.extension && (
            <Tag color="blue">{record.extension}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '大小',
      dataIndex: 'size',
      key: 'size',
      width: 100,
      render: (size: number, record: SaveFile) => 
        record.type === 'file' ? formatFileSize(size) : '-',
    },
    {
      title: '修改时间',
      dataIndex: 'modified',
      key: 'modified',
      width: 180,
      render: (date: string) => (
        <Space>
          <ClockCircleOutlined />
          {formatDate(date)}
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (text: any, record: SaveFile) => (
        <Space>
          {record.type === 'file' && (
            <>
              <Button
                type="link"
                icon={<DownloadOutlined />}
                onClick={() => downloadSaveFile(record.name)}
                size="small"
              >
                下载
              </Button>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => openRenameModal(record)}
                size="small"
                disabled={renameLoading}
              >
                重命名
              </Button>
              <Button
                type="link"
                icon={<ReloadOutlined />}
                onClick={() => {
                  setRestoreFile(record);
                  setRestoreModalVisible(true);
                }}
                size="small"
                danger
                disabled={restoreLoading}
              >
                恢复
              </Button>
            </>
          )}
          <Popconfirm
            title="确定要删除这个文件吗？"
            onConfirm={() => deleteSaveFile(record.name)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              icon={<DeleteOutlined />}
              danger
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedFiles,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedFiles(selectedRowKeys as string[]);
    },
    getCheckboxProps: (record: SaveFile) => ({
      disabled: record.type === 'directory',
    }),
  };

  return (
    <div className="space-y-4">
      {/* 配置信息卡片 */}
      <Card>
        {isMobile ? (
          // 移动端布局
          <div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <DatabaseOutlined style={{ marginRight: '8px' }} />
              <Title level={5} style={{ margin: 0, flex: 1 }}>存档管理</Title>
              <Tag color="blue">{gameType}</Tag>
            </div>
            
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <Button
                icon={<SettingOutlined />}
                onClick={() => setConfigModalVisible(true)}
                size="small"
                style={{ flex: '1', minWidth: '80px' }}
              >
                配置
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchSaveFiles}
                loading={loading}
                size="small"
                style={{ flex: '1', minWidth: '80px' }}
              >
                刷新
              </Button>
            </div>
            
            {gameTypeConfig && (
              <div style={{ fontSize: '12px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <Text strong>存档目录:</Text>
                  <br />
                  <Text code style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                    {gameTypeConfig.save_directory}
                  </Text>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <Text strong>文件后缀:</Text>
                  <br />
                  <Text type="secondary">
                    {fileExtensions.length > 0 ? fileExtensions.join(', ') : '全部文件'}
                  </Text>
                </div>
                <div>
                  <Text strong>显示模式:</Text>
                  <br />
                  <Text type="secondary">
                    {showAllFiles ? '显示所有文件' : '仅显示存档文件'}
                  </Text>
                </div>
              </div>
            )}
          </div>
        ) : (
          // 桌面端布局
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Space>
                <DatabaseOutlined />
                <Title level={5} style={{ margin: 0 }}>存档管理</Title>
                <Tag color="blue">{gameType}</Tag>
              </Space>
            </Col>
            <Col>
              <Space>
                <Button
                  icon={<SettingOutlined />}
                  onClick={() => setConfigModalVisible(true)}
                >
                  配置
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchSaveFiles}
                  loading={loading}
                >
                  刷新
                </Button>
              </Space>
            </Col>
          </Row>
        )}
        
        {gameTypeConfig && !isMobile && (
          <Descriptions size="small" column={3} style={{ marginTop: 16 }}>
            <Descriptions.Item label="存档目录">
              <Text code>{gameTypeConfig.save_directory}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="文件后缀">
              {fileExtensions.length > 0 ? fileExtensions.join(', ') : '全部文件'}
            </Descriptions.Item>
            <Descriptions.Item label="显示模式">
              {showAllFiles ? '显示所有文件' : '仅显示存档文件'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      {/* 操作栏 */}
      {selectedFiles.length > 0 && (
        <Card>
          {isMobile ? (
            <div>
              <div style={{ marginBottom: '12px' }}>
                <Text>已选择 {selectedFiles.length} 个文件</Text>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Popconfirm
                  title={`确定要删除选中的 ${selectedFiles.length} 个文件吗？`}
                  onConfirm={batchDeleteFiles}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button 
                    danger 
                    icon={<DeleteOutlined />}
                    size="small"
                    style={{ flex: '1', minWidth: '100px' }}
                  >
                    批量删除
                  </Button>
                </Popconfirm>
                <Button 
                  onClick={() => setSelectedFiles([])}
                  size="small"
                  style={{ flex: '1', minWidth: '100px' }}
                >
                  取消选择
                </Button>
              </div>
            </div>
          ) : (
            <Space>
              <Text>已选择 {selectedFiles.length} 个文件</Text>
              <Popconfirm
                title={`确定要删除选中的 ${selectedFiles.length} 个文件吗？`}
                onConfirm={batchDeleteFiles}
                okText="确定"
                cancelText="取消"
              >
                <Button danger icon={<DeleteOutlined />}>
                  批量删除
                </Button>
              </Popconfirm>
              <Button onClick={() => setSelectedFiles([])}>
                取消选择
              </Button>
            </Space>
          )}
        </Card>
      )}

      {/* 文件列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredFiles}
          rowKey="name"
          loading={loading}
          rowSelection={isMobile ? undefined : rowSelection}
          pagination={{
            pageSize: isMobile ? 10 : 20,
            showSizeChanger: !isMobile,
            showQuickJumper: !isMobile,
            showTotal: (total) => `共 ${total} 个文件`,
            size: isMobile ? 'small' : 'default',
          }}
          scroll={isMobile ? undefined : { x: 800 }}
          size={isMobile ? 'small' : 'middle'}
        />
      </Card>

      {/* 恢复存档确认对话框 */}
      <Modal
        title="恢复存档"
        open={restoreModalVisible}
        onOk={() => restoreFile && restoreSaveFile(restoreFile.name)}
        onCancel={() => {
          if (!restoreLoading) {
            setRestoreModalVisible(false);
            setRestoreFile(null);
          }
        }}
        okText="确定恢复"
        cancelText="取消"
        okButtonProps={{ 
          danger: true, 
          loading: restoreLoading,
          disabled: restoreLoading
        }}
        cancelButtonProps={{ disabled: restoreLoading }}
        closable={!restoreLoading}
        maskClosable={!restoreLoading}
      >
        {restoreLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, fontSize: '16px' }}>正在恢复存档，请稍候...</p>
            <p style={{ color: '#666', fontSize: '14px' }}>服务器将自动重启，此过程可能需要几分钟</p>
          </div>
        ) : (
          <>
            <Alert
              message="警告"
              description="恢复存档将覆盖当前存档，服务器将自动重启。此操作不可逆，请谨慎操作！"
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            {restoreFile && (
              <div>
                <p><strong>文件名:</strong> {restoreFile.name}</p>
                <p><strong>大小:</strong> {formatFileSize(restoreFile.size)}</p>
                <p><strong>修改时间:</strong> {formatDate(restoreFile.modified)}</p>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* 重命名对话框 */}
      <Modal
        title="重命名文件"
        open={renameModalVisible}
        onOk={renameSaveFile}
        onCancel={() => {
          if (!renameLoading) {
            setRenameModalVisible(false);
            setRenameFile(null);
            setNewFileName('');
          }
        }}
        okText="确定重命名"
        cancelText="取消"
        okButtonProps={{ 
          loading: renameLoading,
          disabled: renameLoading || !newFileName.trim()
        }}
        cancelButtonProps={{ disabled: renameLoading }}
        closable={!renameLoading}
        maskClosable={!renameLoading}
      >
        {renameFile && (
          <div className="space-y-4">
            <Alert
              message="注意事项"
              description="为避免乱码问题，请勿在文件名中使用中文字符。建议使用英文、数字、下划线和连字符。"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <div>
              <label>原文件名:</label>
              <Input
                value={renameFile.name}
                disabled
                style={{ marginTop: 4 }}
              />
            </div>
            
            <div>
              <label>新文件名:</label>
              <Input
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
                placeholder="请输入新的文件名"
                style={{ marginTop: 4 }}
                disabled={renameLoading}
              />
            </div>
            
            <div>
              <Text type="secondary">
                文件大小: {formatFileSize(renameFile.size)} | 
                修改时间: {formatDate(renameFile.modified)}
              </Text>
            </div>
          </div>
        )}
      </Modal>

      {/* 配置对话框 */}
      <Modal
        title="存档管理配置"
        open={configModalVisible}
        onOk={updateGameTypeConfig}
        onCancel={() => setConfigModalVisible(false)}
        width={isMobile ? '90%' : 600}
        style={isMobile ? { top: 20 } : undefined}
      >
        <div className="space-y-4">
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              存档目录:
            </label>
            <Input
              value={gameTypeConfig?.save_directory || ''}
              onChange={(e) => setGameTypeConfig(prev => prev ? { ...prev, save_directory: e.target.value } : null)}
              placeholder="例如: ShooterGame/Saved/SavedArks"
              size={isMobile ? 'small' : 'middle'}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              文件后缀 (用逗号分隔):
            </label>
            <Input
              value={fileExtensions.join(', ')}
              onChange={(e) => setFileExtensions(e.target.value.split(',').map(s => s.trim()).filter(s => s))}
              placeholder="例如: .ark, .sav, .db"
              size={isMobile ? 'small' : 'middle'}
            />
          </div>
          
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Switch
                checked={showAllFiles}
                onChange={setShowAllFiles}
                size={isMobile ? 'small' : 'default'}
              />
              <Text style={{ fontSize: isMobile ? '14px' : '16px' }}>
                显示所有文件 (不限制后缀)
              </Text>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SaveManagement;
